import * as React from "react"
import { Row, Spinner, Button, Table, Modal } from "react-bootstrap"
import { toast } from "react-toastify"
import { concat, of, Subject, Subscription } from "rxjs"
import { catchError, map, switchMap } from "rxjs/operators"
import Clinic from "../../../api/Clinics/Models/Clinic"
import { ClinicGetObservable, ClinicSearchListRemoveObservable, ClinicSearchListAddObservable } from "../../../api/Clinics/ClinicOptionsGet"
import ActionButton from "../../ActionButton"
import SiteSearch from "../../SiteSearch/SiteSearch"
import CollectionSite from "../../../api/Location/LocationModel/CollectionSite"
import OwnedPrimarySites from "./OwnedPrimarySites"

interface ListOptionsProps {
  headerText?: string
  isCompany: boolean
  clinicSearchListTypeID: number
  IsPrimary: boolean
  onCancel: () => void
}

interface ListOptionsState {
  clinics: Clinic[]
  selected: Clinic[]
  loading: boolean
  adding: boolean
  removing: boolean
  showSiteSearchModal: boolean
}

export default class ListOptions extends React.PureComponent<ListOptionsProps, ListOptionsState> {
  state: ListOptionsState = {
    clinics: [],
    selected: [],
    loading: false,
    adding: false,
    removing: false,
    showSiteSearchModal: false
  }

  clinicSearchListTypeID$ = new Subject<number>()

  loadSubscription?: Subscription
  addSubscription?: Subscription
  removeSubscription?: Subscription

  componentDidMount() {
    this.loadSubscription = this.clinicSearchListTypeID$.pipe(
      switchMap(id => concat(
        of({ loading: true }),
        ClinicGetObservable(id).pipe(
          map(result => ({ loading: false, result: result })),
          catchError((error) => of({ loading: false, errorMessage: error.message }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, result?: Clinic[], errorMessage?: string }) => {
        this.setState({ loading: o.loading, selected: [], clinics: o.result || [] })
        if (o.errorMessage) {
          toast.error(o.errorMessage)
        }
      },
      (error) => this.handleAPIError(error, { loading: false, userInfo: undefined })
    )
    this.clinicSearchListTypeID$.next(this.props.clinicSearchListTypeID)
  }

  componentWillUnmount() {
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.addSubscription && this.addSubscription.unsubscribe()
    this.removeSubscription && this.removeSubscription.unsubscribe()
  }

  handleAPIError = (error: Error, stateChanges: object) => {
    this.setState(stateChanges)
    toast.error(error)
  }

  handleClinicClick = (clinic: Clinic) => {
    if (this.state.selected.includes(clinic)) {
      this.setState({ selected: this.state.selected.filter(item => item !== clinic) })
    } else {
      this.setState({ selected: [...this.state.selected, clinic] })
    }
  }

  handleRemove = () => {
    const ids = this.state.selected.map(item => item.ClinicSearchListID)
    this.setState({ removing: true })
    this.removeSubscription = ClinicSearchListRemoveObservable(this.props.clinicSearchListTypeID, ids).subscribe(
      (data) => {
        toast.success(data)
        this.setState({ removing: false })
        this.clinicSearchListTypeID$.next(this.props.clinicSearchListTypeID)
      },
      (error) => this.handleAPIError(error, { removing: false })
    )
  }

  handleAdd = (site: CollectionSite) => {
    this.setState({ adding: true, showSiteSearchModal: false })
    this.addSubscription = ClinicSearchListAddObservable(this.props.clinicSearchListTypeID, site.Code, site.Name).subscribe(
      (data) => {
        toast.success(data)
        this.setState({ adding: false })
        this.clinicSearchListTypeID$.next(this.props.clinicSearchListTypeID)
      },
      (error) => this.handleAPIError(error, { adding: false })
    )
  }

  render() {
    const { headerText } = this.props
    const { clinics, selected, loading, adding, removing, showSiteSearchModal } = this.state
    const disableRemove = selected.length === 0 || (this.props.isCompany && selected.length > 0 && !selected.every(el=> el.AccountID || 0 > 0))

    return <>
      {headerText && <h5 className="mb-0">{headerText}</h5>}
      <Table className={`table-sm dark ${headerText ? "" : "mt-3"}`} borderless responsive>
        <thead>
          <tr>
            <th>Clinic Name</th>
            <th>Clinic Code</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={2} className="text-center"><Spinner animation="border" variant="secondary" className="mb-1" /></td></tr>}
          {!loading && clinics.length === 0 && <tr><td colSpan={2}>NO DATA FOUND</td></tr>}
          {clinics.map(item => <tr key={item.ClinicSearchListID} className={`border-top ${selected.includes(item) ? "selected" : ""}`} onClick={() => this.handleClinicClick(item)}>
            <td>{item.Name}{this.props.isCompany && !item.AccountID ? " (Configured for Portal)" : ""}</td>
            <td>{item.Code}</td>
          </tr>)}
        </tbody>
      </Table>
      <Row className="buttons-block">
        <ActionButton variant="primary" className="mr-1" onClick={() => this.setState({ showSiteSearchModal: true })} spin={adding} disableOnSpin>Site Search</ActionButton>
        <ActionButton variant="secondary" disabled={disableRemove} onClick={this.handleRemove} spin={removing} disableOnSpin>Remove</ActionButton>
        {!this.props.IsPrimary && <Button variant="secondary" onClick={this.props.onCancel} className="ml-1">Cancel</Button>}
      </Row>
      {this.props.IsPrimary && <OwnedPrimarySites onCancel={this.props.onCancel} /> }
      <Modal show={showSiteSearchModal} size="xl" onHide={() => this.setState({ showSiteSearchModal: false })}>
        <Modal.Header closeButton>
          <Modal.Title className="text-uppercase">SITE SEARCH</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SiteSearch onSiteSelect={this.handleAdd} />
        </Modal.Body>
      </Modal>
    </>
  }
}
